import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { SOLUTION_PILLARS, REVENUE_STREAMS, REVENUE_ENGINES, GTM } from "@/data/content";
import { SectionLabel, Reveal } from "@/components/site/primitives";
import { trackClick } from "@/lib/utils";

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

export const BusinessModel = () => {
  return (
    <section
      id="model"
      data-testid="model-section"
      className="mx-auto max-w-[1400px] px-5 md:px-10 py-28 md:py-44"
    >
      <SectionLabel index="[ 02 ]" testId="model-label">
        The Solution & Business Model
      </SectionLabel>

      <Reveal>
        <h2 className="font-grotesk font-black uppercase tracking-tighter text-4xl md:text-6xl lg:text-7xl text-epic-text leading-[0.9] max-w-4xl mb-16 md:mb-24">
          One platform.<br />
          <span className="text-epic-accent">Eleven</span> revenue engines.
        </h2>
      </Reveal>

      {/* Solution pillars */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 mb-4 md:mb-6">
        {SOLUTION_PILLARS.map((p, i) => (
          <Reveal key={p.label} delay={i * 0.08} className="border border-epic-line bg-epic-surface p-6 md:p-8">
            <span className="font-mono text-[11px] text-epic-accent">0{i + 1}</span>
            <h3 className="font-grotesk font-bold uppercase tracking-tight text-xl md:text-2xl text-epic-text mt-4">
              {p.label}
            </h3>
            <p className="font-mono text-sm text-epic-sub mt-3 leading-relaxed">{p.note}</p>
          </Reveal>
        ))}
      </div>

      {/* Revenue streams */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 md:gap-6 mb-4 md:mb-6">
        {REVENUE_STREAMS.map((s, i) => (
          <motion.div
            key={s.label}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] }}
            className="border border-epic-line bg-epic-surface p-6 md:p-8 transition-colors duration-300 hover:border-epic-lineHover"
            data-testid={`revenue-stream-${i}`}
          >
            <p className="font-grotesk font-black text-3xl md:text-4xl text-epic-text tracking-tighter">
              {s.price}
            </p>
            <p className="font-mono text-[11px] uppercase tracking-[0.15em] text-epic-accent mt-1">
              {s.unit}
            </p>
            <p className="font-grotesk font-bold uppercase tracking-tight text-base text-epic-text mt-5">
              {s.label}
            </p>
            <p className="font-mono text-xs text-epic-sub mt-2 leading-relaxed">{s.note}</p>
          </motion.div>
        ))}
      </div>

      {/* Override & referral engines */}
      <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-epic-sub mb-4">
        Plus recurring override & referral engines
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 mb-4 md:mb-6">
        {REVENUE_ENGINES.filter((s) => !s.featured).map((s, i) => {
          const Tag = s.url ? motion.a : motion.div;
          const linkProps = s.url
            ? {
                href: s.url,
                target: "_blank",
                rel: "noopener noreferrer",
                onClick: () => trackClick(`engine-${slug(s.label)}`),
              }
            : {};
          return (
            <Tag
              key={s.label}
              {...linkProps}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] }}
              className={`block border border-epic-line bg-epic-surface p-6 md:p-7 transition-colors duration-300 hover:border-epic-lineHover ${
                s.url ? "cursor-pointer" : ""
              }`}
              data-testid={`revenue-engine-${i}`}
            >
              <p className="font-mono text-[11px] uppercase tracking-[0.15em] text-epic-accent">
                {s.basis}
              </p>
              {s.logo ? (
                <span className="mt-3 flex items-center gap-2">
                  <span className="inline-block bg-white px-3 py-2">
                    <img src={s.logo} alt={s.label} className="h-6 md:h-7 w-auto block" />
                  </span>
                  {s.url && <ArrowUpRight className="w-3.5 h-3.5 text-epic-sub" />}
                </span>
              ) : (
                <p className="font-grotesk font-bold uppercase tracking-tight text-base text-epic-text mt-3 flex items-center gap-2">
                  {s.label}
                  {s.url && <ArrowUpRight className="w-3.5 h-3.5 text-epic-sub" />}
                </p>
              )}
              <p className="font-mono text-xs text-epic-sub mt-2 leading-relaxed">{s.note}</p>
            </Tag>
          );
        })}
      </div>

      {/* Featured engine — full width */}
      {REVENUE_ENGINES.filter((s) => s.featured).map((s) => (
        <motion.div
          key={s.label}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="border border-epic-line border-l-2 border-l-epic-accent bg-epic-surface p-6 md:p-10 mb-4 md:mb-6 transition-colors duration-300 hover:border-epic-lineHover hover:border-l-epic-accent"
          data-testid="revenue-engine-featured"
        >
          <div className="grid md:grid-cols-12 gap-6 md:gap-10">
            <div className="md:col-span-4">
              <p className="font-mono text-[11px] uppercase tracking-[0.15em] text-epic-accent">
                {s.basis}
              </p>
              {s.logo ? (
                <div
                  className="inline-block bg-white px-5 py-4 mt-5"
                  data-testid="revenue-engine-featured-logo"
                >
                  {s.url ? (
                    <a
                      href={s.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="revenue-engine-featured-link"
                      onClick={() => trackClick(`engine-${slug(s.label)}`)}
                      className="block transition-opacity duration-300 hover:opacity-80"
                    >
                      <img src={s.logo} alt={s.label} className="w-44 md:w-52 h-auto block" />
                    </a>
                  ) : (
                    <img src={s.logo} alt={s.label} className="w-44 md:w-52 h-auto block" />
                  )}
                </div>
              ) : (
                <p className="font-grotesk font-bold uppercase tracking-tight text-xl md:text-2xl text-epic-text mt-3">
                  {s.label}
                </p>
              )}
            </div>
            <p className="md:col-span-8 font-mono text-sm text-epic-sub leading-relaxed">
              {s.note}
            </p>
          </div>
        </motion.div>
      ))}

      {/* Go-to-market band */}
      <Reveal className="border border-epic-line bg-epic-accent">
        <div className="p-6 md:p-10">
          <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/80 mb-8">
            Go-To-Market — Advisor / Agent Channel
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {GTM.map((g) => (
              <div key={g.label}>
                <p className="font-grotesk font-black text-4xl md:text-5xl text-white tracking-tighter">
                  {g.value}
                </p>
                <p className="font-mono text-[11px] text-white/80 mt-2 leading-relaxed">{g.label}</p>
              </div>
            ))}
          </div>
        </div>
      </Reveal>
    </section>
  );
};
